import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { useDatabase } from '../contexts/DatabaseContext';
import { CustomerService } from '../services/CustomerService';
import { getAllForms } from '../forms';
import SlideOverDrawer from '../components/common/SlideOverDrawer';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { APPLIANCE_TYPES, FLUE_TYPES, APPLIANCE_STATUS } from '../utils/constants';
import { formatDate } from '../utils/formatters';

const {
  FiMapPin,
  FiUsers,
  FiFileText,
  FiPlus,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiKey,
  FiZap,
  FiAlertCircle,
  FiChevronRight,
  FiCheckCircle
} = FiIcons;

const EMPTY_APPLIANCE = {
  location: '',
  type: APPLIANCE_TYPES.BOILER,
  make: '',
  model: '',
  flueType: '',
  gasMeterSerial: '',
  status: APPLIANCE_STATUS.ACTIVE
};

const EMPTY_FORM = {
  customerId: null,
  type: 'residential',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postcode: '',
  country: 'UK',
  notes: ''
};

const Properties = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeTenant } = useTenant();
  const { isReady } = useDatabase();

  const [allProperties, setAllProperties] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [customerFilter, setCustomerFilter] = useState(searchParams.get('customerId') || 'all');
  const [typeFilter, setTypeFilter] = useState('all');

  // Property editor state
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [formDraft, setFormDraft] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Appliance editing state
  const [applianceEditor, setApplianceEditor] = useState(null); // { property, appliances[] }
  const [applianceDraft, setApplianceDraft] = useState(EMPTY_APPLIANCE);
  const [applianceSaving, setApplianceSaving] = useState(false);

  // Certificate prefill state
  const [prefillFor, setPrefillFor] = useState(null); // property
  const [prefillFormId, setPrefillFormId] = useState('');
  const [prefilling, setPrefilling] = useState(false);
  const [prefillError, setPrefillError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [props, custs] = await Promise.all([
        CustomerService.getProperties(),
        CustomerService.getCustomers()
      ]);
      setAllProperties(props || []);
      setAllCustomers(custs || []);
    } catch (e) {
      console.error('Failed to load properties:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isReady) loadData();
  }, [isReady, loadData]);

  // Query params: ?customerId= preselects customer filter; ?new=1 auto-opens create; ?focus=id opens detail
  useEffect(() => {
    const customerId = searchParams.get('customerId');
    const isNew = searchParams.get('new') === '1';
    const focusId = searchParams.get('focus');

    let paramsChanged = false;
    const params = new URLSearchParams(searchParams);

    if (customerId) setCustomerFilter(customerId);
    if (isNew && isReady && !loading) {
      openCreate(customerId);
      params.delete('new');
      paramsChanged = true;
    }
    if (focusId && isReady && !loading) {
      const target = allProperties.find((p) => String(p.id) === focusId);
      if (target) openDetail(target);
      params.delete('focus');
      paramsChanged = true;
    }
    if (paramsChanged) setSearchParams(params, { replace: true });
  }, [isReady, loading, searchParams, allProperties]);

  const customerById = allCustomers.reduce((map, c) => {
    map[c.id] = c;
    return map;
  }, {});

  const visibleProperties = allProperties.filter((p) => {
    const matchesSearch =
      !searchTerm ||
      [p.addressLine1, p.addressLine2, p.city, p.postcode, (p.appliances || []).map((a) => `${a.make} ${a.model} ${a.type}`).join(' ')]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    const matchesCustomer = customerFilter === 'all' || String(p.customerId) === String(customerFilter);
    const matchesType = typeFilter === 'all' || p.type === typeFilter;
    return matchesSearch && matchesCustomer && matchesType;
  });

  const openCreate = (prefillCustomerId = null) => {
    setEditingProperty(null);
    setFormDraft({
      ...EMPTY_FORM,
      customerId: prefillCustomerId ? Number(prefillCustomerId) : customerFilter !== 'all' ? Number(customerFilter) : null
    });
    setError(null);
    setIsEditorOpen(true);
  };

  const openEdit = (property) => {
    setEditingProperty(property);
    setFormDraft({
      customerId: property.customerId ?? null,
      type: property.type || 'residential',
      addressLine1: property.addressLine1 || '',
      addressLine2: property.addressLine2 || '',
      city: property.city || '',
      postcode: property.postcode || '',
      country: property.country || 'UK',
      notes: property.notes || ''
    });
    setError(null);
    setIsEditorOpen(true);
  };

  const handleFieldChange = (field, value) => {
    setFormDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formDraft.addressLine1.trim() || !formDraft.postcode.trim()) {
      setError('Address line 1 and postcode are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editingProperty) {
        await CustomerService.updateProperty(editingProperty.id, formDraft);
      } else {
        await CustomerService.createProperty(formDraft);
      }
      setIsEditorOpen(false);
      await loadData();
    } catch (e) {
      console.error('Save property failed:', e);
      setError(e.message || 'Failed to save property');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (property) => {
    if (!window.confirm(`Delete property "${property.addressLine1}"? Certificate links remain intact.`)) return;
    try {
      await CustomerService.deleteProperty(property.id);
      setApplianceEditor(null);
      setPrefillFor(null);
      await loadData();
    } catch (e) {
      console.error('Delete property failed:', e);
      window.alert(`Failed to delete property: ${e.message}`);
    }
  };

  // ---- Appliance registry ----
  const openApplianceEditor = (property) => {
    setApplianceEditor(property);
    setApplianceDraft(EMPTY_APPLIANCE);
    setApplianceSaving(false);
  };

  const handleApplianceFieldChange = (field, value) => {
    setApplianceDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddAppliance = async () => {
    if (!applianceDraft.make.trim() && !applianceDraft.model.trim()) {
      window.alert('Enter at least a make or model for the appliance');
      return;
    }
    setApplianceSaving(true);
    try {
      const nextAppliances = [
        ...(applianceEditor.appliances || []),
        { ...applianceDraft, id: `app-${Date.now()}` }
      ];
      await CustomerService.updatePropertyAppliances(applianceEditor.id, nextAppliances);
      setApplianceEditor({ ...applianceEditor, appliances: nextAppliances });
      setApplianceDraft(EMPTY_APPLIANCE);
    } catch (e) {
      console.error('Add appliance failed:', e);
      window.alert(`Failed to add appliance: ${e.message}`);
    } finally {
      setApplianceSaving(false);
    }
  };

  const handleRemoveAppliance = async (applianceId) => {
    setApplianceSaving(true);
    try {
      const nextAppliances = (applianceEditor.appliances || []).filter((a) => a.id !== applianceId);
      await CustomerService.updatePropertyAppliances(applianceEditor.id, nextAppliances);
      setApplianceEditor({ ...applianceEditor, appliances: nextAppliances });
    } catch (e) {
      console.error('Remove appliance failed:', e);
      window.alert(`Failed to remove appliance: ${e.message}`);
    } finally {
      setApplianceSaving(false);
    }
  };

  // ---- Certificate prefill (Track 5.2 form pre-fill hook) ----
  const openPrefill = (property) => {
    setPrefillFor(property);
    setPrefillFormId('');
    setPrefillError(null);
  };

  const availableForms = getAllForms();

  const handlePrefill = async () => {
    if (!prefillFor || !prefillFormId) {
      setPrefillError('Select a certificate type');
      return;
    }
    setPrefilling(true);
    setPrefillError(null);
    try {
      const saved = await CustomerService.createPrefilledForm(prefillFormId, prefillFor.id);
      setPrefillFor(null);
      navigate(`/forms/${prefillFormId}/${saved.id}`);
    } catch (e) {
      console.error('Prefill failed:', e);
      setPrefillError(e.message || 'Failed to create pre-filled certificate');
    } finally {
      setPrefilling(false);
    }
  };

  const openDetail = async (property) => {
    // Open the appliance registry / property panel
    openApplianceEditor(property);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-black tracking-tight text-slate-900 dark:text-slate-100">
              Property & Site Registry
            </h1>
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
              {allProperties.length} Properties
            </span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Organization: <strong className="text-slate-700 dark:text-slate-300">{activeTenant.name}</strong> • Site assets & appliance registry
          </p>
        </div>

        <button
          onClick={() => openCreate()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold shadow-md shadow-blue-900/20 transition-all active:scale-95"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          Add Property
        </button>
      </div>

      {/* Search + Filters */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search properties by address, postcode, appliance..."
            className="w-full pl-9 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-3">
          <select
            className="lg:w-56 w-full py-2 px-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-700 dark:text-slate-200 focus:outline-none"
            value={customerFilter}
            onChange={(e) => {
              setCustomerFilter(e.target.value);
              const params = new URLSearchParams(searchParams);
              if (e.target.value !== 'all') params.set('customerId', e.target.value);
              else params.delete('customerId');
              setSearchParams(params, { replace: true });
            }}
          >
            <option value="all">All Customers</option>
            {allCustomers.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            className="lg:w-44 w-full py-2 px-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-700 dark:text-slate-200 focus:outline-none"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="residential">Residential</option>
            <option value="commercial">Commercial</option>
            <option value="multi-let">Multi-Let</option>
          </select>
        </div>
      </div>

      {/* Property Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
        </div>
      ) : visibleProperties.length === 0 ? (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-12 text-center">
          <SafeIcon icon={FiMapPin} className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1">No properties found</p>
          <p className="text-xs text-slate-400 mb-4">
            {searchTerm || customerFilter !== 'all' || typeFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Register a property to track appliances and generate certificates'}
          </p>
          {!searchTerm && customerFilter === 'all' && typeFilter === 'all' && (
            <button onClick={() => openCreate()} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold">
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              Add Property
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {visibleProperties.map((property) => {
            const linkedCustomer = customerById[property.customerId];
            const applianceCount = property.appliances?.length || 0;
            return (
              <div
                key={property.id}
                onClick={() => openDetail(property)}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-lg transition-all cursor-pointer group"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center text-emerald-600 dark:text-emerald-400 flex-shrink-0">
                      <SafeIcon icon={FiMapPin} className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                        {property.addressLine1}
                      </h3>
                      <p className="text-[10px] text-slate-400 truncate">
                        {[property.addressLine2, property.city, property.postcode].filter(Boolean).join(', ')}
                      </p>
                    </div>
                  </div>
                  <SafeIcon icon={FiChevronRight} className="w-4 h-4 text-slate-300 dark:text-slate-600 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </div>

                <div className="space-y-1.5 text-xs text-slate-500 dark:text-slate-400 mb-4">
                  <p className="flex items-center gap-2 truncate">
                    <SafeIcon icon={FiUsers} className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                    <span className="truncate">{linkedCustomer ? linkedCustomer.name : 'No linked customer'}</span>
                  </p>
                  {applianceCount > 0 && (
                    <p className="flex items-center gap-2">
                      <SafeIcon icon={FiKey} className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      <span>{applianceCount} appliance{applianceCount !== 1 ? 's' : ''}</span>
                    </p>
                  )}
                </div>

                {/* Appliance chips */}
                {(property.appliances || []).filter((a) => a && (a.make || a.model)).slice(0, 3).map((appliance) => (
                  <span
                    key={appliance.id || `${appliance.make}-${appliance.model}`}
                    className="inline-block mr-1 mb-1 text-[9px] font-bold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                  >
                    {[appliance.make, appliance.model].filter(Boolean).join(' ')}
                  </span>
                ))}

                <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-800 mt-3">
                  <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400">
                    {formatDate(property.createdAt)}
                  </span>
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => openPrefill(property)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 hover:bg-blue-100 dark:hover:bg-blue-950/70 transition-colors"
                      title="Create certificate pre-filled from this property"
                    >
                      <SafeIcon icon={FiFileText} className="w-3 h-3" />
                      Certificate
                    </button>
                    <button
                      onClick={() => openEdit(property)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/40 transition-colors"
                      title="Edit Property"
                    >
                      <SafeIcon icon={FiEdit2} className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(property)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                      title="Delete Property"
                    >
                      <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit Property Drawer */}
      <SlideOverDrawer
        isOpen={isEditorOpen}
        onClose={() => !saving && setIsEditorOpen(false)}
        title={editingProperty ? 'Edit Property' : 'Add Property'}
        subtitle={editingProperty ? `Updating ${editingProperty.addressLine1}` : 'Register a new site / property asset'}
        width="max-w-xl"
        footer={
          <div className="w-full flex justify-end gap-2">
            <button
              onClick={() => !saving && setIsEditorOpen(false)}
              className="px-4 py-2 rounded-lg text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Saving...' : editingProperty ? 'Save Changes' : 'Add Property'}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 text-xs border border-red-200 dark:border-red-900">
              <SafeIcon icon={FiAlertCircle} className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Linked Customer</label>
            <select
              value={formDraft.customerId ?? ''}
              onChange={(e) => handleFieldChange('customerId', e.target.value ? Number(e.target.value) : null)}
              className="w-full py-2 px-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-700 dark:text-slate-200 focus:outline-none"
            >
              <option value="">No customer (unassigned)</option>
              {allCustomers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <Link
              to="/customers"
              className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 mt-1"
            >
              <SafeIcon icon={FiUsers} className="w-3 h-3" />
              Manage customers
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Property Type</label>
              <select
                value={formDraft.type}
                onChange={(e) => handleFieldChange('type', e.target.value)}
                className="w-full py-2 px-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-700 dark:text-slate-200 focus:outline-none"
              >
                <option value="residential">Residential</option>
                <option value="commercial">Commercial</option>
                <option value="multi-let">Multi-Let</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Country</label>
              <input
                type="text"
                value={formDraft.country}
                onChange={(e) => handleFieldChange('country', e.target.value)}
                className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Address Line 1 *</label>
            <input
              type="text"
              value={formDraft.addressLine1}
              onChange={(e) => handleFieldChange('addressLine1', e.target.value)}
              placeholder="12 Acacia Avenue"
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Address Line 2</label>
            <input
              type="text"
              value={formDraft.addressLine2}
              onChange={(e) => handleFieldChange('addressLine2', e.target.value)}
              placeholder="Apartment / building name"
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">City</label>
              <input
                type="text"
                value={formDraft.city}
                onChange={(e) => handleFieldChange('city', e.target.value)}
                placeholder="London"
                className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Postcode *</label>
              <input
                type="text"
                value={formDraft.postcode}
                onChange={(e) => handleFieldChange('postcode', e.target.value)}
                placeholder="SW1A 1AA"
                className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Notes</label>
            <textarea
              value={formDraft.notes}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
              rows={2}
              placeholder="Internal notes about this property"
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
            />
          </div>
        </div>
      </SlideOverDrawer>

      {/* Appliance Registry Drawer (also acts as property detail) */}
      <SlideOverDrawer
        isOpen={!!applianceEditor}
        onClose={() => {
          setApplianceEditor(null);
          setPrefillFor(null);
        }}
        title={applianceEditor ? applianceEditor.addressLine1 : ''}
        subtitle={applianceEditor ? `${[applianceEditor.city, applianceEditor.postcode].filter(Boolean).join(', ')} • ${applianceEditor.appliances?.length || 0} appliances` : ''}
        width="max-w-2xl"
        footer={
          applianceEditor && (
            <div className="w-full flex items-center justify-between gap-2">
              <button
                onClick={() => {
                  setPrefillFor(applianceEditor);
                  setPrefillFormId('');
                  setPrefillError(null);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold text-white bg-blue-600 hover:bg-blue-500 transition-colors"
              >
                <SafeIcon icon={FiFileText} className="w-3.5 h-3.5" />
                Create Certificate
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openEdit(applianceEditor)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                >
                  <SafeIcon icon={FiEdit2} className="w-3.5 h-3.5" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(applianceEditor)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 hover:bg-red-100 dark:hover:bg-red-950/80 transition-colors"
                >
                  <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                  Delete
                </button>
              </div>
            </div>
          )
        }
      >
        {applianceEditor && (
          <div className="space-y-6">
            {/* Linked customer info */}
            {(() => {
              const cust = customerById[applianceEditor.customerId];
              if (!cust) return null;
              return (
                <div className="bg-slate-50 dark:bg-slate-800/60 rounded-lg p-3 text-xs">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Linked Customer</p>
                  <p className="font-semibold text-slate-700 dark:text-slate-200">{cust.name}</p>
                  {(cust.email || cust.phone) && (
                    <p className="text-slate-500 dark:text-slate-400">{cust.email}{cust.email && cust.phone ? ' • ' : ''}{cust.phone}</p>
                  )}
                </div>
              );
            })()}

            {/* Appliance Registry */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                  Appliance Registry ({(applianceEditor.appliances || []).length})
                </h4>
              </div>

              {/* Existing appliances */}
              <div className="space-y-2 mb-3">
                {(applianceEditor.appliances || []).length === 0 ? (
                  <p className="text-xs text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-slate-800/60 rounded-lg p-3">
                    No appliances registered yet. Add the boiler, meter or cylinder details below.
                  </p>
                ) : (
                  (applianceEditor.appliances || []).map((appliance) => (
                    <div key={appliance.id || `${appliance.make}-${appliance.model}`} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-300 flex-shrink-0">
                          {appliance.type === 'Electrical' || appliance.type === APPLIANCE_TYPES.BOILER ? (
                            <SafeIcon icon={FiZap} className="w-4 h-4" />
                          ) : (
                            <SafeIcon icon={FiKey} className="w-4 h-4" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">
                            {[appliance.make, appliance.model].filter(Boolean).join(' ') || 'Unnamed appliance'}
                          </p>
                          <p className="text-[10px] text-slate-400 truncate">
                            {[appliance.type, appliance.flueType, appliance.location].filter(Boolean).join(' • ')}
                            {appliance.gasMeterSerial ? ` • Meter: ${appliance.gasMeterSerial}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${
                          appliance.status === APPLIANCE_STATUS.DECOMMISSIONED
                            ? 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                            : 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300'
                        }`}>
                          {appliance.status === APPLIANCE_STATUS.DECOMMISSIONED ? 'Decommissioned' : 'Active'}
                        </span>
                        <button
                          onClick={() => handleRemoveAppliance(appliance.id)}
                          disabled={applianceSaving}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                          title="Remove appliance"
                        >
                          <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Add appliance form */}
              <div className="bg-slate-50 dark:bg-slate-800/60 rounded-lg p-3 space-y-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Register New Appliance</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 mb-0.5">Type</label>
                    <select
                      value={applianceDraft.type}
                      onChange={(e) => handleApplianceFieldChange('type', e.target.value)}
                      className="w-full py-1.5 px-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-xs text-slate-700 dark:text-slate-200 focus:outline-none"
                    >
                      {Object.values(APPLIANCE_TYPES).map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 mb-0.5">Location</label>
                    <input
                      type="text"
                      value={applianceDraft.location}
                      onChange={(e) => handleApplianceFieldChange('location', e.target.value)}
                      placeholder="Kitchen"
                      className="w-full py-1.5 px-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 mb-0.5">Make</label>
                    <input
                      type="text"
                      value={applianceDraft.make}
                      onChange={(e) => handleApplianceFieldChange('make', e.target.value)}
                      placeholder="Worcester Bosch"
                      className="w-full py-1.5 px-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 mb-0.5">Model</label>
                    <input
                      type="text"
                      value={applianceDraft.model}
                      onChange={(e) => handleApplianceFieldChange('model', e.target.value)}
                      placeholder="Greenstar 30CDi"
                      className="w-full py-1.5 px-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 mb-0.5">Flue Type</label>
                    <select
                      value={applianceDraft.flueType}
                      onChange={(e) => handleApplianceFieldChange('flueType', e.target.value)}
                      className="w-full py-1.5 px-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-xs text-slate-700 dark:text-slate-200 focus:outline-none"
                    >
                      <option value="">—</option>
                      {Object.values(FLUE_TYPES).map((flue) => (
                        <option key={flue} value={flue}>{flue}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 mb-0.5">Gas Meter Serial</label>
                    <input
                      type="text"
                      value={applianceDraft.gasMeterSerial}
                      onChange={(e) => handleApplianceFieldChange('gasMeterSerial', e.target.value)}
                      placeholder="G4S123456"
                      className="w-full py-1.5 px-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none"
                    />
                  </div>
                </div>
                <button
                  onClick={handleAddAppliance}
                  disabled={applianceSaving}
                  className="w-full inline-flex items-center justify-center gap-2 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <SafeIcon icon={FiPlus} className="w-3.5 h-3.5" />
                  {applianceSaving ? 'Saving...' : 'Add Appliance'}
                </button>
              </div>
            </div>

            {/* Certificate history for this property */}
            <PropertyCertificateHistory propertyId={applianceEditor.id} />
          </div>
        )}
      </SlideOverDrawer>

      {/* Certificate Prefill Drawer */}
      <SlideOverDrawer
        isOpen={!!prefillFor}
        onClose={() => {
          setPrefillFor(null);
          setPrefillError(null);
        }}
        title="Create Certificate"
        subtitle={prefillFor ? `Pre-filling from ${prefillFor.addressLine1}` : ''}
        width="max-w-lg"
        footer={
          <div className="w-full flex justify-end gap-2">
            <button
              onClick={() => {
                setPrefillFor(null);
                setPrefillError(null);
              }}
              className="px-4 py-2 rounded-lg text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handlePrefill}
              disabled={prefilling}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <SafeIcon icon={FiFileText} className="w-3.5 h-3.5" />
              {prefilling ? 'Creating...' : 'Create & Open'}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          {prefillError && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 text-xs border border-red-200 dark:border-red-900">
              <SafeIcon icon={FiAlertCircle} className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{prefillError}</span>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Certificate Type *</label>
            <select
              value={prefillFormId}
              onChange={(e) => setPrefillFormId(e.target.value)}
              className="w-full py-2 px-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-700 dark:text-slate-200 focus:outline-none"
            >
              <option value="">Select a certificate type...</option>
              {availableForms.map((form) => (
                <option key={form.id} value={form.id}>
                  {form.icon} {form.name}
                </option>
              ))}
            </select>
          </div>

          <div className="bg-blue-50 dark:bg-blue-950/40 rounded-lg p-3 text-xs text-blue-700 dark:text-blue-300">
            <p className="font-semibold mb-1 flex items-center gap-1.5">
              <SafeIcon icon={FiCheckCircle} className="w-3.5 h-3.5" />
              Automatic pre-fill
            </p>
            <ul className="list-disc pl-4 space-y-0.5 text-[11px]">
              <li>Property address, city & postcode</li>
              <li>Linked customer name, email & phone</li>
              <li>Registered appliances (make, model, flue type)</li>
            </ul>
            <p className="text-[10px] mt-1.5 opacity-80">A blank draft is created and linked to this property for syncing. You can edit any field before signing.</p>
          </div>
        </div>
      </SlideOverDrawer>
    </div>
  );
};

/** Certificate history list for a property (fetches on mount). */
const PropertyCertificateHistory = ({ propertyId }) => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const records = await CustomerService.getPropertyCertificates(propertyId);
        if (mounted) setCertificates(records || []);
      } catch (e) {
        console.error('Failed to load property certificates:', e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [propertyId]);

  return (
    <div>
      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">
        Certificate History ({certificates.length})
      </h4>
      {loading ? (
        <p className="text-xs text-slate-400">Loading certificates...</p>
      ) : certificates.length === 0 ? (
        <p className="text-xs text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-slate-800/60 rounded-lg p-3">
          No certificates for this property yet. Use "Create Certificate" above to generate a pre-filled draft.
        </p>
      ) : (
        <div className="space-y-2">
          {certificates.map((cert) => (
            <Link
              key={cert.id}
              to={`/forms/${cert.formId}/${cert.id}`}
              className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-800 hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-950/30 transition-all group"
            >
              <div className="flex items-center gap-3 min-w-0">
                <SafeIcon icon={FiFileText} className="w-4 h-4 text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">
                    {cert.instanceId || `${cert.formId} #${cert.id}`}
                  </p>
                  <p className="text-[10px] text-slate-400">{formatDate(cert.updatedAt)}</p>
                </div>
              </div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                cert.status === 'completed' || cert.status === 'issued'
                  ? 'bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-300'
                  : 'bg-yellow-100 dark:bg-yellow-950/40 text-yellow-700 dark:text-yellow-300'
              }`}>
                {cert.status}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Properties;